import React, { useState, useRef } from "react";
import {
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Loader,
  Maximize2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import {
  prepareFileForUpload,
  createFilePreview,
} from "../../utils/fileUpload";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const MultiFileUpload = ({
  onUpload,
  currentFiles = [],
  onDelete = null,
  accept = "image/*,application/pdf",
  maxSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 10,
  label = "Dosyalar",
  description = "WEBP, JPG, PNG veya PDF (Max 10MB, Max 10 dosya)",
  convertToWebP = true,
  className = "",
}) => {
  const [files, setFiles] = useState(
    currentFiles.map((f, i) => ({
      id: `existing-${i}`,
      preview: typeof f === "string" ? f : f.url || f.preview,
      name: f.name || "Dosya",
      type: f.type || (typeof f === "string" && f.includes(".pdf") ? "application/pdf" : "image/*"),
      size: f.size || 0,
      status: "existing",
      url: typeof f === "string" ? f : f.url,
    }))
  );
  const [fileStatuses, setFileStatuses] = useState({});
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    if (files.length + selectedFiles.length > maxFiles) {
      setError(`En fazla ${maxFiles} dosya yükleyebilirsiniz`);
      return;
    }

    setError(null);

    for (const file of selectedFiles) {
      const fileId = crypto.randomUUID();
      
      // Initial placeholder state
      const newFileObj = {
        id: fileId,
        preview: null,
        name: file.name,
        type: file.type,
        size: file.size,
        status: "preparing",
      };

      setFiles((prev) => [...prev, newFileObj]);
      setFileStatuses((prev) => ({ ...prev, [fileId]: "preparing" }));

      try {
        // Step 1: Prepare (WebP conversion etc.)
        const processedFile = await prepareFileForUpload(file, {
          maxSize,
          convertToWebP: convertToWebP && file.type.startsWith("image/"),
          resize: file.type.startsWith("image/"),
        });

        // Step 2: Preview
        const previewUrl = await createFilePreview(processedFile);

        // Update status to uploading
        setFileStatuses((prev) => ({ ...prev, [fileId]: "uploading" }));
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: "uploading", preview: previewUrl, file: processedFile }
              : f
          )
        );

        // Step 3: Upload callback
        if (onUpload) {
          const result = await onUpload(processedFile);
          
          // Update status to success
          setFileStatuses((prev) => ({ ...prev, [fileId]: "success" }));
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { 
                    ...f, 
                    status: "success", 
                    url: result?.url || result,
                    id: result?.id || f.id 
                  }
                : f
            )
          );

          // Success indicator for 2 seconds
          setTimeout(() => {
            setFileStatuses((prev) => {
              const newStatuses = { ...prev };
              delete newStatuses[fileId];
              return newStatuses;
            });
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileId || (result?.id && f.id === result.id)
                  ? { ...f, status: "existing" }
                  : f
              )
            );
          }, 2000);
        }
      } catch (err) {
        console.error("File upload error:", err);
        setFileStatuses((prev) => ({ ...prev, [fileId]: "error" }));
        setFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: "error" } : f))
        );
        setError(err.message);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (index) => {
    await onDelete(index);
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const renderFilePreview = (fileData, index) => {
    const fileUrl = fileData.preview || fileData.url;
    const status = fileStatuses[fileData.id] || fileData.status;
    const isUploading = status === "uploading" || status === "preparing";
    const isSuccess = status === "success";
    const isError = status === "error";

    const overlayContent = () => {
      if (status === "preparing") {
        return (
          <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center text-white z-10">
            <Loader size={24} className="animate-spin mb-1" />
            <span className="text-[10px]">Hazırlanıyor...</span>
          </div>
        );
      }
      if (status === "uploading") {
        return (
          <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center text-white z-10">
            <Loader size={24} className="animate-spin mb-1" />
            <span className="text-[10px]">Yükleniyor...</span>
          </div>
        );
      }
      if (isSuccess) {
        return (
          <div className="absolute inset-0 bg-green-500 bg-opacity-40 rounded-lg flex flex-col items-center justify-center text-white z-10">
            <CheckCircle size={24} className="mb-1" />
            <span className="text-[10px]">Tamamlandı</span>
          </div>
        );
      }
      if (isError) {
        return (
          <div className="absolute inset-0 bg-red-500 bg-opacity-40 rounded-lg flex flex-col items-center justify-center text-white z-10">
            <AlertCircle size={24} className="mb-1" />
            <span className="text-[10px]">Hata!</span>
          </div>
        );
      }
      return null;
    };

    const isPdf = fileData.type === "application/pdf" || (typeof fileUrl === "string" && fileUrl.includes(".pdf"));

    if (isPdf) {
      return (
        <div key={fileData.id || index} className="relative group">
          <div className="flex flex-col items-center justify-center h-32 bg-red-50 rounded-lg border-2 border-red-200 p-2 hover:bg-red-100 transition-colors">
            <FileText size={40} className="text-red-500 mb-2" />
            <p className="text-xs text-gray-600 text-center truncate w-full px-2">
              {fileData.name || "PDF Dosya"}
            </p>
          </div>
          {overlayContent()}
          {!isUploading && (
            <button
              onClick={() => handleDelete(index)}
              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-20"
            >
              <X size={14} />
            </button>
          )}
        </div>
      );
    }

    return (
      <div key={fileData.id || index} className="relative group">
        <div
          className="relative cursor-pointer"
          onClick={() => !isUploading && handleImageClick(index)}
        >
          <img
            src={fileUrl}
            alt={fileData.name || "Preview"}
            className={`w-full h-32 object-cover rounded-lg border-2 border-gray-200 group-hover:border-primary-500 transition-all ${
              isUploading ? "blur-[1px]" : ""
            }`}
          />
          {overlayContent()}
          {!isUploading && !isSuccess && !isError && (
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
              <Maximize2
                size={24}
                className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          )}
        </div>
        {!isUploading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(index);
            }}
            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-20"
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        multiple
      />

      {/* Upload Area */}
      <div
        onClick={handleClick}
        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
      >
        {Object.keys(fileStatuses).length > 0 ? (
          <div className="flex flex-col items-center">
            <Loader size={40} className="text-primary-600 animate-spin mb-2" />
            <p className="text-sm text-gray-600">
              {Object.keys(fileStatuses).length} dosya işleniyor...
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload size={40} className="text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              Dosya seçmek için tıklayın veya sürükleyin
            </p>
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {files.filter(f => f.status === 'existing').length} / {maxFiles} dosya yüklendi
            </p>
          </div>
        )}
      </div>

      {/* File Grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {files.map((fileData, index) => renderFilePreview(fileData, index))}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={files
          .filter((f) => {
            const url = f.preview || f.url;
            return !(
              f.type === "application/pdf" ||
              (typeof url === "string" && url.includes(".pdf"))
            );
          })
          .map((f) => ({
            src: f.preview || f.url,
            alt: f.name || "Image",
          }))}
      />
    </div>
  );
};

export default MultiFileUpload;
